import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Flex,
  Button,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  FormErrorMessage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
  Stack,
  Checkbox,
  Alert,
  AlertIcon,
  Icon,
  VisuallyHidden
} from '@chakra-ui/react';
import { addContact, addCompany } from '../../../shared/components/functions';
import { useField } from 'formik';
import ContactForms from './newContact';
import CombinedForm from './newCompany';
import { FiCamera } from 'react-icons/fi';

export const fairOutlineButtonProps = {
  variant: 'outline',
  borderRadius: 'full',
  color: 'white',
  borderColor: 'whiteAlpha.500',
  transition: 'all 160ms ease',
  _hover: { bg: 'whiteAlpha.200', borderColor: 'whiteAlpha.800' },
  _active: { bg: 'whiteAlpha.300' },
  _disabled: { opacity: 0.55, cursor: 'not-allowed' }
};

export const fairModalButtonProps = {
  variant: 'outline',
  borderRadius: 'full',
  color: 'gray.900',
  borderColor: 'gray.300',
  transition: 'all 160ms ease',
  _hover: { bg: 'gray.50', borderColor: 'gray.500' },
  _active: { bg: 'gray.100' }
};

export const TextInput = ({ label, ...props }) => {
  const [field, meta] = useField(props);
  return (
    <FormControl isRequired={props.isRequired} aria-required="true" isInvalid={meta.touched && meta.error}>
      <FormLabel htmlFor={props.id || props.name}>{label}</FormLabel>
      <Input className="text-input" {...field} {...props} />
      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
};

export const SelectInput = ({ label, options, ...props }) => {
  const [field, meta] = useField(props);
  return (
    <FormControl isRequired={props.isRequired} aria-required="true" isInvalid={meta.touched && meta.error}>
      <FormLabel htmlFor={props.id || props.name}>{label}</FormLabel>
      <Select
        {...field}
        {...props}
        sx={{
          '& option': {
            backgroundColor: 'white',
            color: 'black',
            padding: '8px 12px'
          },
          '& option:hover': {
            backgroundColor: '#f7fafc',
            color: 'black'
          },
          '& option:checked': {
            backgroundColor: '#3182ce',
            color: 'white'
          }
        }}
      >
        {options}
      </Select>
      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
};

export const TextareaInput = ({ label, ...props }) => {
  const [field, meta] = useField(props);
  return (
    <FormControl aria-required="true" isInvalid={meta.touched && meta.error}>
      <FormLabel htmlFor={props.id || props.name}>{label}</FormLabel>
      <Textarea className="text-input" {...field} {...props} />
      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
};

export const CheckboxInput = ({ label, ...props }) => {
  const [field, meta] = useField(props);
  return (
    <FormControl isInvalid={meta.touched && meta.error}>
      <FormLabel htmlFor={props.id || props.name}>{label}</FormLabel>
      <Stack spacing={5} direction="column">
        <Checkbox size="lg" id="Greg" onChange={props.handleChange}>
          Greg
        </Checkbox>
        <Checkbox size="lg" id="Jack" onChange={props.handleChange}>
          Jack
        </Checkbox>
        <Checkbox size="lg" id="dealership" onChange={props.handleChange}>
          Dealership
        </Checkbox>
        <Checkbox size="lg" id="newsletter" onChange={props.handleChange}>
          Newsletter
        </Checkbox>
      </Stack>
      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
};

export const BusinessTypeCheckboxInput = ({ label, ...props }) => {
  const [field, meta] = useField(props);
  return (
    <FormControl isInvalid={meta.touched && meta.error}>
      <FormLabel htmlFor={props.id || props.name}>{label}</FormLabel>
      <Stack spacing={3} direction="column">
        <Checkbox size="lg" id="roaster" onChange={props.handleChange}>
          Roaster
        </Checkbox>
        <Checkbox size="lg" id="single-coffee-shop" onChange={props.handleChange}>
          Single coffee shop / independent café
        </Checkbox>
        <Checkbox size="lg" id="coffee-chain" onChange={props.handleChange}>
          Coffee chain (2+ locations)
        </Checkbox>
        <Checkbox size="lg" id="equipment-reseller" onChange={props.handleChange}>
          Equipment reseller / distributor
        </Checkbox>
        <Checkbox size="lg" id="barista" onChange={props.handleChange}>
          Barista
        </Checkbox>
        <Checkbox size="lg" id="coffee-trainer" onChange={props.handleChange}>
          Coffee trainer
        </Checkbox>
        <Checkbox size="lg" id="coffee-consultant" onChange={props.handleChange}>
          Coffee consultant
        </Checkbox>
        <Checkbox size="lg" id="horeca" onChange={props.handleChange}>
          Hotel / restaurant / hospitality operator (HORECA)
        </Checkbox>
        <Checkbox size="lg" id="catering" onChange={props.handleChange}>
          Catering business
        </Checkbox>
        <Checkbox size="lg" id="manufacturer" onChange={props.handleChange}>
          Manufacturer / OEM partner
        </Checkbox>
        <Checkbox size="lg" id="importer-exporter" onChange={props.handleChange}>
          Importer / exporter
        </Checkbox>
        <Checkbox size="lg" id="publicist-influencer" onChange={props.handleChange}>
          Publicist / influencer
        </Checkbox>
        <Stack spacing={3} direction="row" align="center">
          <Checkbox size="lg" id="other-business" onChange={props.handleChange}>
            Other:
          </Checkbox>
          <Input
            placeholder="Please specify"
            size="sm"
            width="200px"
            onChange={(e) => {
              if (e.target.value) {
                props.handleChange({ target: { checked: true, id: 'other-business' } });
              }
              // Store the other value separately
              props.setOtherValue?.(e.target.value);
            }}
          />
        </Stack>
      </Stack>
      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
};

export default function CombinedForms({ newCompany, newContact, company, isLoading, setIsLoading, pubs }) {
  const location = useLocation();
  const toast = useToast();
  const [isDisabled, setIsDisabled] = useState(false);
  const { isOpen, onOpen } = useDisclosure();
  const [prefillData, setPrefillData] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState('');
  const fileInputRef = useRef(null);

  const handleScanClick = () => {
    setOcrError('');
    fileInputRef.current?.click();
  };

  const handleImageSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setOcrLoading(true);
    setOcrError('');

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch('/api/openai/process-lead-image', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        let message = 'Failed to process image';
        try {
          const errorBody = await response.json();
          message = errorBody.error || errorBody.details || message;
        } catch (jsonError) {
          // ignore json parsing errors, fallback to default message
        }
        throw new Error(message);
      }

      const data = await response.json();
      if (!data?.lead) {
        throw new Error('AI service returned no data');
      }

      setPrefillData({ ...data.lead, _timestamp: Date.now() });
      toast({
        title: 'Lead details captured',
        description: 'We pre-filled the form with details from the photo. Please double-check everything before submitting.',
        status: 'success',
        duration: 5000,
        isClosable: true
      });
    } catch (error) {
      console.error('Lead image processing failed:', error);
      const description = error.message || 'Unexpected error while processing image';
      setOcrError(description);
      toast({
        title: 'Could not extract details',
        description,
        status: 'error',
        duration: 6000,
        isClosable: true
      });
    } finally {
      setOcrLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  async function handleSubmit(values) {
    if (newCompany) {
      addCompany({ ...values, companyNAAM: company.NAAM }, pubs, setIsLoading, toast, setIsDisabled, onOpen, location);
    } else {
      addContact({ ...values, KLANR: company.FIRNR }, pubs, setIsLoading, toast, setIsDisabled, onOpen, location);
    }
  }

  const SubmitButton = (
    <Flex direction="row-reverse">
      <Button disabled={isDisabled} type="submit" isLoading={isLoading} size="lg" {...fairOutlineButtonProps}>
        Submit
      </Button>
    </Flex>
  );

  const combinedForm = newCompany ? <CombinedForm handleSubmit={handleSubmit} SubmitButton={SubmitButton} prefillData={prefillData} /> : null;
  const onlyContact = newContact && !newCompany ? <ContactForms handleSubmit={handleSubmit} SubmitButton={SubmitButton} prefillData={prefillData} /> : null;

  return (
    <div>
      <VisuallyHidden>
        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleImageSelected} />
      </VisuallyHidden>

      <Stack spacing={3} mb={6}>
        <Flex justify="flex-end">
          <Button size="lg" leftIcon={<Icon as={FiCamera} />} onClick={handleScanClick} isLoading={ocrLoading} loadingText="Processing" {...fairOutlineButtonProps}>
            Scan card with AI
          </Button>
        </Flex>
        {ocrError ? (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {ocrError}
          </Alert>
        ) : null}
      </Stack>

      {combinedForm}
      {onlyContact}
      <Modal closeOnOverlayClick={true} isOpen={isOpen} onClose={() => (window.location.href = `/fairs/form${location.search}`)}>
        <ModalOverlay color="white" bgColor={useColorModeValue('sm', 'sm-dark')} />
        <ModalContent p={3}>
          <ModalHeader>Thank you! 🎉 🎉 </ModalHeader>
          <ModalCloseButton />
          <ModalBody>We successfully saved your information.</ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              size="lg"
              {...fairModalButtonProps}
              onClick={() => {
                window.location.href = `/fairs/form${location.search}`;
              }}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
